import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Plus, Trash2, Award, Download, Upload, FileSpreadsheet, Settings, X, Pencil, Cake, Heart, Baby, Star, Calendar, Briefcase, Database, FileText } from 'lucide-react';

const WORKLOAD = [
  { value: '', label: 'Not set' },
  { value: 'green', label: 'Healthy', color: '#2D8B5E' },
  { value: 'amber', label: 'Stretched', color: '#BA7517' },
  { value: 'red', label: 'Overloaded', color: '#D4726A' },
];

export default function PeopleTab({ divisionId, divisionName }) {
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showOutletMgr, setShowOutletMgr] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [inlineEdit, setInlineEdit] = useState(null);  // { id, dept, mobile, email, reports_to }
  const [savingInline, setSavingInline] = useState(false);
  const [filterOutlet, setFilterOutlet] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [searchText, setSearchText] = useState('');
  const [allDivisions, setAllDivisions] = useState([]);
  const fileRef = useRef(null);
  const salaryFileRef = useRef(null);
  const dojFileRef = useRef(null);
  const formRef = useRef(null);
  const [newOutlet, setNewOutlet] = useState({ name: '', location: '' });
  const [milestones, setMilestones] = useState([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [outletSummary, setOutletSummary] = useState(null);

  const emptyForm = {
    emp_no: '', name: '', role: '', location: '', date_joined: '',
    reports_to: '', reports_to_dotted: '', entity: divisionName || '',
    notes: '', status: 'active', outlet: '', department: '',
    satisfaction_score: '', workload_flag: '', development_goals: '',
    career_aspiration: '', family_needs: '', personal_notes: '',
    transfer_division: '',
  };
  const [form, setForm] = useState(emptyForm);

  const refresh = async () => {
    const [pRes, oRes, dRes] = await Promise.all([
      api.get(`/people?division_id=${divisionId}`),
      api.get(`/outlets?division_id=${divisionId}`),
      api.get('/divisions'),
    ]);
    setPeople(pRes.data);
    setOutlets(oRes.data);
    setAllDivisions(dRes.data.map ? dRes.data : []);
    // Fetch upcoming milestones (30 days)
    try {
      const mRes = await api.get(`/people/upcoming-milestones?division_id=${divisionId}&days=30`);
      setMilestones(mRes.data.milestones || []);
    } catch { setMilestones([]); }
    try {
      const sRes = await api.get(`/people/outlet-summary?division_id=${divisionId}`);
      setOutletSummary(sRes.data);
    } catch { setOutletSummary(null); }
  };

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [divisionId]);

  const departments = [...new Set(people.map(p => p.department).filter(Boolean))].sort();
  const peopleOutlets = [...new Set(people.map(p => p.outlet).filter(Boolean))].sort();
  const locations = [...new Set([...outlets.map(o => o.location), ...people.map(p => p.location)].filter(Boolean))].sort();

  const resetForm = () => { setForm(emptyForm); setEditId(null); setShowForm(false); };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, division_id: divisionId };
    if (form.transfer_division && form.transfer_division !== divisionId) {
      payload.division_id = form.transfer_division;
    }
    delete payload.transfer_division;
    if (editId) await api.put(`/people/${editId}`, payload);
    else await api.post('/people', payload);
    resetForm(); refresh();
  };

  const handleEdit = (p) => {
    navigate(`/people/${p.id}`);
  };

  const startInline = (p) => {
    setInlineEdit({
      id: p.id,
      department: p.department || '',
      mobile: p.mobile || p.phone_personal || '',
      email: p.work_email || p.personal_email || p.email_personal || '',
      reports_to: p.reports_to || '',
    });
  };
  const cancelInline = () => setInlineEdit(null);
  const saveInline = async () => {
    if (!inlineEdit) return;
    setSavingInline(true);
    try {
      await api.put(`/people/${inlineEdit.id}`, {
        department: inlineEdit.department,
        mobile: inlineEdit.mobile,
        work_email: inlineEdit.email,
        reports_to: inlineEdit.reports_to,
      });
      setInlineEdit(null);
      await refresh();
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    } finally { setSavingInline(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('division_id', divisionId);
    try {
      const r = await api.post('/people/upload-csv', fd);
      alert(`Imported ${r.data.imported} people${r.data.errors?.length ? `. Errors: ${r.data.errors.join(', ')}` : ''}`);
      refresh();
    } catch { alert('Upload failed'); }
    e.target.value = '';
  };

  const handleHRTemplate = async () => {
    const r = await api.get(`/people/hr-master/csv-template`, { responseType: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
    a.download = 'hr_master_template.csv'; a.click();
  };
  const handleHRExport = async () => {
    const r = await api.get(`/people/hr-master/export-csv?division_id=${divisionId}`, { responseType: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
    a.download = `hr_master_${divisionId}_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };
  const hrFileRef = useRef(null);
  const handleHRImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!window.confirm('Import HR master? This will update matching people (by emp_no or name) and create new people for unmatched rows.')) { e.target.value = ''; return; }
    const fd = new FormData(); fd.append('file', file); fd.append('division_id', divisionId || '');
    try {
      const r = await api.post('/people/hr-master/import-csv', fd);
      alert(`HR Master import complete.\n  Created: ${r.data.created}\n  Updated: ${r.data.updated}${r.data.errors?.length ? '\n  Errors: ' + r.data.errors.slice(0, 5).join('\n  ') : ''}`);
      await refresh();
    } catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); }
    finally { e.target.value = ''; }
  };

  const handleDownload = async (template = false) => {
    const url = template ? '/people/csv-template' : `/people/download-csv?division_id=${divisionId}`;
    const r = await api.get(url, { responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([r.data]));
    a.download = template ? 'people_template.csv' : `people_${divisionId}.csv`; a.click();
  };

  const handleDojExport = async () => {
    const r = await api.get(`/people/doj-export?division_id=${divisionId}`, { responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([r.data]));
    a.download = `people_doj_${divisionId}.csv`; a.click();
  };

  const handleSalaryTemplate = async () => {
    const r = await api.get(`/people/salary-template?division_id=${divisionId}`, { responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([r.data]));
    a.download = `salary_template_${divisionId}.csv`; a.click();
  };

  const handleSalaryImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/people/salary-import', fd);
      alert(`Salary data imported: ${r.data.updated} updated${r.data.not_found?.length ? `, ${r.data.not_found.length} not found` : ''}`);
      refresh();
    } catch { alert('Salary import failed'); }
    e.target.value = '';
  };

  const handleDojImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/people/doj-import', fd);
      alert(`DOJ updated: ${r.data.updated} records${r.data.not_found?.length ? `, ${r.data.not_found.length} not found` : ''}`);
      refresh();
    } catch { alert('DOJ import failed'); }
    e.target.value = '';
  };

  const addOutlet = async () => {
    if (!newOutlet.name.trim()) return;
    await api.post('/outlets', { ...newOutlet, division_id: divisionId });
    setNewOutlet({ name: '', location: '' });
    const oRes = await api.get(`/outlets?division_id=${divisionId}`);
    setOutlets(oRes.data);
  };

  const deleteOutlet = async (id) => {
    if (!window.confirm('Delete this outlet?')) return;
    await api.delete(`/outlets/${id}`);
    const oRes = await api.get(`/outlets?division_id=${divisionId}`);
    setOutlets(oRes.data);
  };

  const filtered = people.filter(p => {
    if (filterOutlet && p.outlet !== filterOutlet) return false;
    if (filterDept && p.department !== filterDept) return false;
    if (filterLocation && p.location !== filterLocation) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      return (p.name || '').toLowerCase().includes(q)
        || (p.role || '').toLowerCase().includes(q)
        || (p.emp_no || '').toLowerCase().includes(q)
        || (p.department || '').toLowerCase().includes(q)
        || (p.outlet || '').toLowerCase().includes(q)
        || (p.location || '').toLowerCase().includes(q)
        || (p.reports_to || '').toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) return <div className="py-8 text-center text-sm" style={{ color: '#6B7280' }}>Loading team...</div>;

  return (
    <div className="space-y-4" data-testid="people-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>My People ({people.length})</h4>
          <p className="text-xs" style={{ color: '#6B7280' }}>{filtered.length !== people.length ? `Showing ${filtered.length} of ${people.length}` : `Team roster for ${divisionName}`}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowOutletMgr(!showOutletMgr)} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="manage-outlets-btn"><Settings size={12} /> Outlets</button>
          <button onClick={openAddForm} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-person-btn"><Plus size={13} /> Add Person</button>
        </div>
      </div>

      {/* HR Master — prominent, this is THE template to use for full HR data */}
      <div className="rounded-xl border-2 p-3" style={{ borderColor: '#185FA5', backgroundColor: '#EFF6FF' }} data-testid="hr-master-panel">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-bold flex items-center gap-1.5" style={{ color: '#185FA5' }}>
              <Database size={14} /> HR Master — All HR data in one CSV
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: '#1B2A4A' }}>
              68-column template covering personal, contact, IDs, bank, statutory (PF/ESI/PT), compensation, education, family, kit sizing, development notes. Download → fill in Excel → re-upload. Matches by <code className="bg-white px-1 rounded">emp_no</code> or name; blanks don't overwrite existing data.
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={handleHRTemplate} className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-semibold text-white" style={{ backgroundColor: '#185FA5' }} data-testid="hr-template-btn"><FileText size={11} /> Download Template</button>
            <button onClick={handleHRExport} className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] border hover:bg-white" style={{ borderColor: '#185FA5', color: '#185FA5' }} data-testid="hr-export-btn"><Download size={11} /> Export Current Data</button>
            <button onClick={() => hrFileRef.current?.click()} className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-semibold text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="hr-import-btn"><Upload size={11} /> Import HR Master</button>
            <input ref={hrFileRef} type="file" accept=".csv" className="hidden" onChange={handleHRImport} />
          </div>
        </div>
      </div>

      {/* Import/Export toolbar */}
      <div className="flex flex-wrap items-center gap-1.5">
        <button onClick={handleDojExport} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="doj-export-btn"><Calendar size={11} /> DOJ Export</button>
        <button onClick={() => dojFileRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="doj-import-btn"><Upload size={11} /> Import DOJ</button>
        <input ref={dojFileRef} type="file" accept=".csv" className="hidden" onChange={handleDojImport} />
        <span className="text-[#E5E1DC]">|</span>
        <button onClick={handleSalaryTemplate} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#2A7B88' }} data-testid="salary-template-btn"><Download size={11} /> Salary Template</button>
        <button onClick={() => salaryFileRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border hover:bg-gray-50" style={{ borderColor: '#2A7B88', color: '#2A7B88' }} data-testid="salary-import-btn"><Upload size={11} /> Import Salary</button>
        <input ref={salaryFileRef} type="file" accept=".csv" className="hidden" onChange={handleSalaryImport} />
        <span className="text-[#E5E1DC]">|</span>
        <button onClick={() => handleDownload(false)} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="people-csv-download"><Download size={11} /> Export All</button>
        <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="people-csv-upload"><Upload size={11} /> Import People</button>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleUpload} />
      </div>

      {/* Outlet-wise Summary */}
      {outletSummary && outletSummary.outlets.length > 0 && (
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }} data-testid="outlet-summary">
          <div className="px-3 py-2 flex items-center justify-between" style={{ backgroundColor: '#F7F5F2' }}>
            <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{['lc', 'cc', 'cc-clubs', 'cc-ops', 'cs', 'cs-b2b', 'cs-b2c'].includes(divisionId) ? 'Cost-Centre Headcount' : 'Outlet-wise Headcount'} ({outletSummary.total_headcount})</span>
          </div>
          <table className="w-full text-[11px]">
            <thead><tr style={{ backgroundColor: '#FAFAF8' }}>
              <th className="text-left px-3 py-1.5 font-medium" style={{ color: '#6B7280' }}>Outlet</th>
              <th className="text-right px-3 py-1.5 font-medium" style={{ color: '#6B7280' }}>Headcount</th>
              <th className="text-left px-3 py-1.5 font-medium" style={{ color: '#6B7280' }}>Departments</th>
              {outletSummary.can_see_salary && <th className="text-right px-3 py-1.5 font-medium" style={{ color: '#6B7280' }}>Monthly Cost</th>}
              {outletSummary.can_see_salary && <th className="text-right px-3 py-1.5 font-medium" style={{ color: '#6B7280' }}>Annual Cost</th>}
              {outletSummary.can_see_salary && <th className="text-right px-3 py-1.5 font-medium" style={{ color: '#6B7280' }}>Revenue Target</th>}
              {outletSummary.can_see_salary && <th className="text-right px-3 py-1.5 font-medium" style={{ color: '#6B7280' }}>Labour %</th>}
            </tr></thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {outletSummary.outlets.map((o, i) => (
                <tr key={i} className="hover:bg-[#FAFAF8]">
                  <td className="px-3 py-1.5 font-medium cursor-pointer hover:text-[#2A7B88] hover:underline" style={{ color: '#1B2A4A' }}
                    onClick={() => navigate(`/division/${divisionId}/outlet/${encodeURIComponent(o.outlet)}`)}>{o.outlet}</td>
                  <td className="px-3 py-1.5 text-right font-semibold" style={{ color: '#2A7B88' }}>{o.headcount}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {o.departments.map((d, di) => (
                        <button key={di} onClick={() => { setFilterOutlet(o.outlet); setFilterDept(d.name); }}
                          className="text-[10px] px-1.5 py-0.5 rounded-full hover:bg-[#E5E1DC] transition-colors"
                          style={{ backgroundColor: (filterOutlet === o.outlet && filterDept === d.name) ? '#2A7B8820' : '#F7F5F2', color: (filterOutlet === o.outlet && filterDept === d.name) ? '#2A7B88' : '#6B7280' }}
                          data-testid={`dept-chip-${o.outlet}-${d.name}`.replace(/\s+/g, '-')}>
                          {d.name} ({d.count})
                        </button>
                      ))}
                    </div>
                  </td>
                  {outletSummary.can_see_salary && <td className="px-3 py-1.5 text-right" style={{ color: '#6B7280' }}>{o.total_salary_monthly > 0 ? `${(o.total_salary_monthly/100000).toFixed(1)}L` : '—'}</td>}
                  {outletSummary.can_see_salary && <td className="px-3 py-1.5 text-right" style={{ color: '#6B7280' }}>{o.total_salary_annual > 0 ? `${(o.total_salary_annual/10000000).toFixed(2)}Cr` : '—'}</td>}
                  {outletSummary.can_see_salary && <td className="px-3 py-1.5 text-right" style={{ color: '#6B7280' }}>{o.revenue_target > 0 ? `${(o.revenue_target/10000000).toFixed(2)}Cr` : '—'}</td>}
                  {outletSummary.can_see_salary && <td className="px-3 py-1.5 text-right">
                    {o.labour_cost_pct > 0 ? (
                      <span className="font-semibold text-[10px] px-1.5 py-0.5 rounded-full" style={{
                        backgroundColor: o.labour_cost_pct > 30 ? '#FEF2F2' : o.labour_cost_pct > 25 ? '#FEF3C7' : '#ECFDF5',
                        color: o.labour_cost_pct > 30 ? '#D4726A' : o.labour_cost_pct > 25 ? '#BA7517' : '#2D8B5E',
                      }}>{o.labour_cost_pct}%</span>
                    ) : '—'}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="people-filters">
        <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search name, role, department, outlet, location..." className="px-2.5 py-1 rounded text-xs border w-56" style={{ borderColor: '#E5E1DC' }} data-testid="search-people" />
        {peopleOutlets.length > 0 && (
          <select value={filterOutlet} onChange={e => setFilterOutlet(e.target.value)} className="px-2 py-1 rounded text-xs border" style={{ borderColor: '#E5E1DC' }} data-testid="filter-outlet">
            <option value="">All outlets ({peopleOutlets.length})</option>
            {peopleOutlets.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        {locations.length > 0 && (
          <select value={filterLocation} onChange={e => setFilterLocation(e.target.value)} className="px-2 py-1 rounded text-xs border" style={{ borderColor: '#E5E1DC' }} data-testid="filter-location">
            <option value="">All locations</option>
            {locations.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        )}
        {departments.length > 0 && (
          <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="px-2 py-1 rounded text-xs border" style={{ borderColor: '#E5E1DC' }} data-testid="filter-department">
            <option value="">All departments ({departments.length})</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-2 py-1 rounded text-xs border" style={{ borderColor: '#E5E1DC' }} data-testid="filter-status">
          <option value="active">Active only</option>
          <option value="ex">Ex-employees</option>
          <option value="">All statuses</option>
        </select>
        {(filterOutlet || filterDept || filterLocation || searchText || filterStatus !== 'active') && (
          <button onClick={() => { setFilterOutlet(''); setFilterDept(''); setFilterLocation(''); setSearchText(''); setFilterStatus('active'); }} className="px-2 py-1 rounded text-xs hover:bg-gray-100" style={{ color: '#D4726A' }}>Clear all</button>
        )}
      </div>

      {/* Outlet Manager */}
      {showOutletMgr && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="outlet-manager">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Manage Outlets ({outlets.length})</p>
            <button onClick={() => setShowOutletMgr(false)}><X size={14} style={{ color: '#6B7280' }} /></button>
          </div>
          <div className="flex gap-2">
            <input value={newOutlet.name} onChange={e => setNewOutlet(p => ({ ...p, name: e.target.value }))} placeholder="Outlet name" className="flex-1 px-3 py-1.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-outlet-name" />
            <input value={newOutlet.location} onChange={e => setNewOutlet(p => ({ ...p, location: e.target.value }))} placeholder="Location" className="w-32 px-3 py-1.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-outlet-location" />
            <button onClick={addOutlet} className="px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-outlet-btn">Add</button>
          </div>
          <div className="space-y-1">
            {outlets.map(o => (
              <div key={o.id} className="flex items-center justify-between px-2 py-1.5 rounded text-xs" style={{ backgroundColor: '#F7F5F2' }}>
                <span className="font-medium" style={{ color: '#1B2A4A' }}>{o.name} <span style={{ color: '#6B7280' }}>({o.location})</span></span>
                <button onClick={() => deleteOutlet(o.id)} className="p-0.5 hover:bg-gray-200 rounded"><Trash2 size={12} style={{ color: '#D4726A' }} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Milestones — all types */}
      {(() => {
        const hasMilestones = milestones.length > 0;
        const tenureMilestones = people.filter(p => p.days_to_milestone && p.days_to_milestone <= 90);
        const recentCompletions = people.filter(p => p.recently_completed_milestone);
        const hasPersonalData = people.some(p => p.birthday || p.wedding_anniversary);
        const showBanner = hasMilestones || tenureMilestones.length > 0 || recentCompletions.length > 0;

        const MILESTONE_CONFIG = {
          birthday: { icon: Cake, color: '#E879A0', bg: '#FDF2F8', label: 'Birthday' },
          'wedding anniversary': { icon: Heart, color: '#D4726A', bg: '#FEF2F2', label: 'Anniversary' },
          work_anniversary: { icon: Award, color: '#BA7517', bg: '#FFFBEB', label: 'Work Anniversary' },
          child_birthday: { icon: Baby, color: '#7C3AED', bg: '#F5F3FF', label: 'Family' },
          custom: { icon: Star, color: '#2A7B88', bg: '#ECFDF5', label: 'Custom' },
          default: { icon: Calendar, color: '#6B7280', bg: '#F3F4F6', label: 'Milestone' },
        };

        const getConfig = (type) => {
          if (type?.includes('birthday') && type !== 'birthday') return MILESTONE_CONFIG.child_birthday;
          if (type?.startsWith('custom')) return MILESTONE_CONFIG.custom;
          if (type?.includes('spouse')) return MILESTONE_CONFIG.child_birthday;
          return MILESTONE_CONFIG[type] || MILESTONE_CONFIG.default;
        };

        return (
          <div className="space-y-0" data-testid="milestones-section">
            {showBanner && (
              <div className="rounded-lg border p-4 space-y-2" style={{ backgroundColor: '#FEFDF8', borderColor: '#F3E8C7' }}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Upcoming Milestones</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                    Next 30 days
                  </span>
                </div>

                {/* Rich milestones from personal details */}
                {milestones.map((m, i) => {
                  const cfg = getConfig(m.type);
                  const Icon = cfg.icon;
                  return (
                    <div key={`m-${i}`}
                      className="flex items-center gap-2.5 py-1.5 px-2 rounded-md cursor-pointer hover:shadow-sm transition-shadow"
                      style={{ backgroundColor: cfg.bg }}
                      onClick={() => m.person_id && navigate(`/people/${m.person_id}`)}
                      data-testid={`milestone-${m.type}-${i}`}
                    >
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color + '20' }}>
                        <Icon size={12} style={{ color: cfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{m.message}</p>
                      </div>
                      <span className="text-[10px] font-medium flex-shrink-0 px-1.5 py-0.5 rounded" style={{
                        color: m.days_away === 0 ? '#fff' : m.days_away <= 3 ? '#92400E' : '#6B7280',
                        backgroundColor: m.days_away === 0 ? cfg.color : m.days_away <= 3 ? '#FEF3C7' : '#F3F4F6',
                      }}>
                        {m.days_away === 0 ? 'Today' : `${m.days_away}d`}
                      </span>
                    </div>
                  );
                })}

                {/* Tenure completions (from people data, not milestone endpoint) */}
                {recentCompletions.map(p => (
                  <div key={`c-${p.id}`}
                    className="flex items-center gap-2.5 py-1.5 px-2 rounded-md cursor-pointer hover:shadow-sm transition-shadow"
                    style={{ backgroundColor: '#ECFDF5' }}
                    onClick={() => navigate(`/people/${p.id}`)}
                  >
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2D8B5E20' }}>
                      <Award size={12} style={{ color: '#2D8B5E' }} />
                    </div>
                    <p className="text-xs font-medium" style={{ color: '#1B2A4A' }}><strong>{p.name}</strong> completed <strong>{p.recently_completed_milestone} years</strong> with us</p>
                  </div>
                ))}

                {/* Upcoming tenure milestones not covered by API (91+ days away, only show if no rich milestones) */}
                {milestones.length === 0 && tenureMilestones.map(p => (
                  <div key={`u-${p.id}`}
                    className="flex items-center gap-2.5 py-1.5 px-2 rounded-md cursor-pointer hover:shadow-sm transition-shadow"
                    style={{ backgroundColor: '#FFFBEB' }}
                    onClick={() => navigate(`/people/${p.id}`)}
                  >
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#BA751720' }}>
                      <Award size={12} style={{ color: '#BA7517' }} />
                    </div>
                    <p className="text-xs font-medium" style={{ color: '#1B2A4A' }}><strong>{p.name}</strong> — {p.tenure_milestone_next}yr in {p.days_to_milestone} days</p>
                    <span className="text-[10px] font-medium flex-shrink-0 px-1.5 py-0.5 rounded" style={{ color: '#6B7280', backgroundColor: '#F3F4F6' }}>{p.days_to_milestone}d</span>
                  </div>
                ))}

                {/* No milestones at all */}
                {milestones.length === 0 && tenureMilestones.length === 0 && recentCompletions.length === 0 && (
                  <p className="text-xs py-2" style={{ color: '#9CA3AF' }}>No upcoming milestones in the next 30 days.</p>
                )}
              </div>
            )}

            {/* Prompt to fill in personal details */}
            {!hasPersonalData && people.length > 0 && (
              <div className="rounded-md p-3 border" style={{ backgroundColor: '#F7F5F2', borderColor: '#E5E1DC' }} data-testid="fill-details-prompt">
                <p className="text-xs" style={{ color: '#6B7280' }}>
                  <span className="font-medium" style={{ color: '#1B2A4A' }}>Fill in birthdays and anniversaries</span> in employee profiles to see more milestones here.
                  Click any name in the table below → Personal tab → add their birthday, anniversary, and family details.
                </p>
              </div>
            )}
          </div>
        );
      })()}

      {/* Add/Edit Form */}
      {showForm && (
        <form ref={formRef} onSubmit={handleSubmit} className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="person-form">
          <p className="text-xs font-semibold pb-1 border-b" style={{ color: '#6B7280', borderColor: '#E5E1DC' }}>Add New Employee</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Emp No</label><input value={form.emp_no} onChange={e => setForm(p => ({...p, emp_no: e.target.value}))} placeholder="e.g. 1001" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="person-empno" /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Full Name *</label><input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="person-name" /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Designation</label><input value={form.role} onChange={e => setForm(p => ({...p, role: e.target.value}))} placeholder="e.g. CDP, Manager" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="person-role" /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Department</label><select value={form.department} onChange={e => setForm(p => ({...p, department: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="person-department"><option value="">Select department...</option>{departments.map(d => <option key={d} value={d}>{d}</option>)}</select></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Outlet</label><select value={form.outlet} onChange={e => setForm(p => ({...p, outlet: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="person-outlet"><option value="">Select outlet...</option>{[...new Set([...outlets.map(o=>o.name), ...peopleOutlets])].sort().map(o => <option key={o} value={o}>{o}</option>)}</select></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Location</label><input value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} list="loc-list" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="person-location" /><datalist id="loc-list">{locations.map(l => <option key={l} value={l} />)}<option value="Work from Home" /></datalist></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Date Joined</label><input type="date" value={form.date_joined} onChange={e => setForm(p => ({...p, date_joined: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="person-joined" /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Reports To</label><input value={form.reports_to} onChange={e => setForm(p => ({...p, reports_to: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Dotted Line To</label><input value={form.reports_to_dotted} onChange={e => setForm(p => ({...p, reports_to_dotted: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Status</label><select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}><option value="active">Active</option><option value="inactive">Inactive (left)</option></select></div>
            <div className="sm:col-span-3"><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Notes</label><input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="person-submit">Add Employee</button>
            <button type="button" onClick={resetForm} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </form>
      )}

      {/* People Table */}
      <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC', marginBottom: '80px' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Emp#</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Name</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Designation</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Department</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Outlet</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Location</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden md:table-cell" style={{ color: '#6B7280' }}>Mobile</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden md:table-cell" style={{ color: '#6B7280' }}>Email</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden lg:table-cell" style={{ color: '#6B7280' }}>Reports To</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden lg:table-cell" style={{ color: '#6B7280' }}>DOJ</th>
              <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr></thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {filtered.map(p => {
                const isInline = inlineEdit && inlineEdit.id === p.id;
                return (
                <tr key={p.id} data-testid={`person-row-${p.id}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-xs" style={{ color: '#9CA3AF' }}>{p.emp_no || '-'}</td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-xs cursor-pointer hover:underline" style={{ color: '#1B2A4A' }} onClick={() => navigate(`/people/${p.id}`)} data-testid={`person-name-${p.emp_no || p.id}`}>{p.name}</p>
                    {p.days_to_milestone && p.days_to_milestone <= 90 && (
                      <span className="text-[10px] px-1 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>{p.tenure_milestone_next}yr in {p.days_to_milestone}d</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{p.role || '-'}</td>
                  <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>
                    {isInline ? (
                      <input value={inlineEdit.department} onChange={e => setInlineEdit({ ...inlineEdit, department: e.target.value })} className="w-24 px-1.5 py-0.5 rounded border text-xs" style={{ borderColor: '#2A7B88' }} placeholder="Department" />
                    ) : (p.department || '-')}
                  </td>
                  <td className="px-3 py-2 text-xs cursor-pointer hover:text-[#2A7B88] hover:underline" style={{ color: '#6B7280' }}
                    onClick={() => p.outlet && navigate(`/division/${divisionId}/outlet/${encodeURIComponent(p.outlet)}`)}>{p.outlet || '-'}</td>
                  <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{p.location || '-'}</td>
                  <td className="px-3 py-2 text-xs hidden md:table-cell" style={{ color: '#6B7280' }}>
                    {isInline ? (
                      <input value={inlineEdit.mobile} onChange={e => setInlineEdit({ ...inlineEdit, mobile: e.target.value })} className="w-28 px-1.5 py-0.5 rounded border text-xs" style={{ borderColor: '#2A7B88' }} placeholder="Mobile" />
                    ) : (p.mobile || p.phone_personal) ? <a href={`tel:${p.mobile || p.phone_personal}`} className="hover:text-[#2A7B88] hover:underline">{p.mobile || p.phone_personal}</a> : <span style={{ color: '#D4726A' }}>— missing</span>}
                  </td>
                  <td className="px-3 py-2 text-xs hidden md:table-cell" style={{ color: '#6B7280' }}>
                    {isInline ? (
                      <input value={inlineEdit.email} onChange={e => setInlineEdit({ ...inlineEdit, email: e.target.value })} className="w-44 px-1.5 py-0.5 rounded border text-xs" style={{ borderColor: '#2A7B88' }} placeholder="Email" />
                    ) : (p.work_email || p.personal_email || p.email_personal) ? (
                      <a href={`mailto:${p.work_email || p.personal_email || p.email_personal}`} className="hover:text-[#2A7B88] hover:underline truncate block max-w-[160px]" title={p.work_email || p.personal_email || p.email_personal}>{p.work_email || p.personal_email || p.email_personal}</a>
                    ) : <span style={{ color: '#D4726A' }}>— missing</span>}
                  </td>
                  <td className="px-3 py-2 text-xs hidden lg:table-cell" style={{ color: '#6B7280' }}>
                    {isInline ? (
                      <input value={inlineEdit.reports_to} onChange={e => setInlineEdit({ ...inlineEdit, reports_to: e.target.value })} className="w-32 px-1.5 py-0.5 rounded border text-xs" style={{ borderColor: '#2A7B88' }} placeholder="Reports to" />
                    ) : (p.reports_to || '-')}
                  </td>
                  <td className="px-3 py-2 text-xs hidden lg:table-cell" style={{ color: '#9CA3AF' }}>{p.date_joined || '-'}</td>
                  <td className="px-3 py-2 text-right">
                    {isInline ? (
                      <span className="inline-flex gap-1">
                        <button onClick={saveInline} disabled={savingInline} className="px-2 py-1 text-xs rounded text-white disabled:opacity-50" style={{ backgroundColor: '#2D8B5E' }} data-testid={`save-inline-${p.emp_no || p.id}`}>{savingInline ? '…' : 'Save'}</button>
                        <button onClick={cancelInline} className="px-2 py-1 text-xs rounded border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
                      </span>
                    ) : (
                      <span className="inline-flex gap-1">
                        <button onClick={(e) => { e.stopPropagation(); startInline(p); }} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-blue-50" style={{ color: '#2A7B88' }} data-testid={`quick-edit-person-${p.emp_no || p.id}`} title="Quick-edit Department, Mobile, Email, Reports To"><Pencil size={11} /> Quick</button>
                        <button onClick={(e) => { e.stopPropagation(); handleEdit(p); }} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-blue-50" style={{ color: '#6B7280' }} data-testid={`edit-person-${p.emp_no || p.id}`}>Full</button>
                      </span>
                    )}
                  </td>
                </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>
                  {people.length > 0 ? 'No results for current filter.' : 'No team members yet.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
